/* ------------------------------------------------------------------
   EmailBlockEditor.js — a block-based email designer.
   Drag elements from the palette onto the canvas; reorder by dragging.
   Blocks: heading / text / image / button / divider / spacer / columns
   (multi-column). Each renders to email-safe, inline-styled HTML.
   Exports renderBlocksToHtml() (used by the composer to produce the sent
   body) + a template gallery. Images upload to S3 via /api/upload.
------------------------------------------------------------------ */
import React, { useRef, useState } from 'react';
import axios from 'axios';

const ax = axios.create({ withCredentials: true });
const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const nl2br = s => esc(s).replace(/\n/g, '<br>');

/* ── block factories ── */
export const blockDefaults = {
  heading: { type: 'heading', text: 'Your headline', level: 1, align: 'left' },
  text:    { type: 'text', text: 'Write something your readers will care about…', align: 'left' },
  image:   { type: 'image', url: '', alt: '', href: '', align: 'center', width: 100 },
  button:  { type: 'button', text: 'Click here', href: 'https://', align: 'left', bg: '#2563eb', color: '#ffffff' },
  divider: { type: 'divider' },
  spacer:  { type: 'spacer', height: 24 },
  columns: { type: 'columns', ratio: '50-50', bg: '', columns: [[{ type: 'text', text: 'Left column', align: 'left' }], [{ type: 'text', text: 'Right column', align: 'left' }]] },
};
const BLOCK_LABEL = { heading: 'Heading', text: 'Text', image: 'Image', button: 'Button', divider: 'Divider', spacer: 'Spacer', columns: 'Columns' };
const newBlock = (type) => JSON.parse(JSON.stringify(blockDefaults[type]));
const RATIOS = { '100': ['100%'], '50-50': ['50%', '50%'], '33-67': ['33%', '67%'], '67-33': ['67%', '33%'], '33-33-33': ['33.33%', '33.33%', '33.33%'], '25-25-25-25': ['25%', '25%', '25%', '25%'] };
const LAYOUTS = [
  { key: '100', count: 1, parts: [1] },
  { key: '50-50', count: 2, parts: [1, 1] },
  { key: '33-67', count: 2, parts: [1, 2] },
  { key: '67-33', count: 2, parts: [2, 1] },
  { key: '33-33-33', count: 3, parts: [1, 1, 1] },
  { key: '25-25-25-25', count: 4, parts: [1, 1, 1, 1] },
];
const makeColumns = (L) => ({ type: 'columns', ratio: L.key, bg: '', columns: Array.from({ length: L.count }, () => []) });

/* ── render to email-safe HTML (preview=true draws placeholders for empty images) ── */
export function renderBlocksToHtml(blocks, preview = false) {
  const inner = (blocks || []).map(b => renderBlock(b, preview)).join('\n');
  return `<div style="font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:16px;line-height:1.6;color:#111827;max-width:600px;margin:0 auto">\n${inner}\n</div>`;
}
function renderBlock(b, preview = false) {
  const align = ['left', 'center', 'right'].includes(b.align) ? b.align : 'left';
  switch (b.type) {
    case 'heading': {
      const size = Number(b.level) === 2 ? 22 : 28;
      const tag = Number(b.level) === 2 ? 'h2' : 'h1';
      return `<${tag} style="margin:0 0 14px;font-size:${size}px;line-height:1.25;color:#111827;text-align:${align}">${esc(b.text)}</${tag}>`;
    }
    case 'text':
      return `<p style="margin:0 0 16px;text-align:${align}">${nl2br(b.text)}</p>`;
    case 'image': {
      if (!b.url) return preview ? `<div style="background:#e5e7eb;height:90px;border-radius:4px;margin:0 0 16px"></div>` : '';
      const w = Math.min(Math.max(Number(b.width) || 100, 10), 100);
      const img = `<img src="${esc(b.url)}" alt="${esc(b.alt)}" style="width:${w}%;max-width:100%;border:0;display:inline-block" />`;
      const wrapped = b.href ? `<a href="${esc(b.href)}" target="_blank" rel="noreferrer">${img}</a>` : img;
      return `<div style="text-align:${align};margin:0 0 16px">${wrapped}</div>`;
    }
    case 'button': {
      const href = esc(b.href || '#');
      return `<div style="text-align:${align};margin:0 0 18px"><a href="${href}" target="_blank" rel="noreferrer" style="display:inline-block;background:${esc(b.bg)};color:${esc(b.color)};padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">${esc(b.text)}</a></div>`;
    }
    case 'divider':
      return `<hr style="border:0;border-top:1px solid #e5e7eb;margin:20px 0" />`;
    case 'spacer':
      return `<div style="height:${Math.max(Number(b.height) || 0, 0)}px;line-height:1px">&nbsp;</div>`;
    case 'columns': {
      const cols = (b.columns && b.columns.length) ? b.columns : [[], []];
      const widths = RATIOS[b.ratio] || RATIOS['50-50'];
      const pad = b.bg ? 16 : 0;                 // inset the colored band so content isn't flush to the edge
      const tds = cols.map((col, i) => {
        const padR = i < cols.length - 1 ? 6 : 0, padL = i > 0 ? 6 : 0;
        return `<td width="${widths[i] || (100 / cols.length) + '%'}" valign="top" style="vertical-align:top;padding:0 ${padR}px 0 ${padL}px">${(col || []).map(sb => renderBlock(sb, preview)).join('')}</td>`;
      }).join('');
      const inner = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%"><tr>${tds}</tr></table>`;
      if (b.bg) {
        return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;margin:0 0 16px"><tr><td bgcolor="${esc(b.bg)}" style="background:${esc(b.bg)};border-radius:10px;padding:${pad}px">${inner}</td></tr></table>`;
      }
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;margin:0 0 16px">${`<tr>${tds}</tr>`}</table>`;
    }
    default: return '';
  }
}

/* ── starter templates ── */
const H = (text, o = {}) => ({ type: 'heading', text, level: 1, align: 'left', ...o });
const T = (text, o = {}) => ({ type: 'text', text, align: 'left', ...o });
const IMG = (alt = 'Image') => ({ type: 'image', url: '', alt, href: '', align: 'center', width: 100 });
const BTN = (text, o = {}) => ({ type: 'button', text, href: 'https://', align: 'left', bg: '#2563eb', color: '#ffffff', ...o });
const DIV = () => ({ type: 'divider' });
const SP = (height = 16) => ({ type: 'spacer', height });
const COLS = (columns, o = {}) => ({ type: 'columns', ratio: '50-50', bg: '', columns, ...o });
const FOOT = (text) => T(text, { align: 'center' });

export const TEMPLATES = [
  { name: 'Simple newsletter', blocks: [
    H('📣 The Monthly Roundup'),
    T('Hi {{name}},\n\nHere’s the latest news, a quick tip, and what’s coming next month.'),
    DIV(),
    H('Top story', { level: 2 }), IMG('Story image'),
    T('A short paragraph introducing your main story or update goes here. Keep it tight and lead with the value.'),
    BTN('Read the full story'),
    SP(8),
    H('Quick hits', { level: 2 }),
    COLS([[T('• Update one\nA sentence of detail.')], [T('• Update two\nA sentence of detail.')]]),
    DIV(),
    FOOT('You’re receiving this because you subscribed. Unsubscribe anytime.'),
  ] },
  { name: 'Sell a product', blocks: [
    H('Just landed', { align: 'center' }),
    COLS([
      [IMG('Product')],
      [H('Product name', { level: 2 }), T('A punchy line about why they’ll love it — the benefit, not just the feature.'), BTN('Shop now', { bg: '#111827' })],
    ], { bg: '#f8fafc' }),
    SP(8),
    T('Free shipping this week with code SHIP.', { align: 'center' }),
    BTN('Browse the collection', { align: 'center' }),
  ] },
  { name: 'Product showcase', blocks: [
    H('Shop the collection', { align: 'center' }),
    T('Hand-picked favorites, just for you.', { align: 'center' }),
    COLS([
      [IMG('Item A'), H('Item A', { level: 2, align: 'center' }), T('$00', { align: 'center' }), BTN('Buy', { align: 'center' })],
      [IMG('Item B'), H('Item B', { level: 2, align: 'center' }), T('$00', { align: 'center' }), BTN('Buy', { align: 'center' })],
    ]),
    SP(8),
    BTN('See everything', { align: 'center' }),
  ] },
  { name: 'Tell a story', blocks: [
    IMG('Hero'),
    H('A story worth telling'),
    T('Open with something personal. Pull the reader in, then build toward your point.'),
    T('Wrap up with the takeaway and one clear next step.'),
    BTN('Continue reading'),
    DIV(),
    FOOT('Forwarded to you? Subscribe for more.'),
  ] },
  { name: 'Event invite', blocks: [
    H('You’re invited 🎉', { align: 'center' }),
    T('Join us for an event you won’t want to miss.', { align: 'center' }),
    IMG('Event'),
    COLS([
      [T('📅 When\nMonth 00, 0:00 PM', { align: 'center' })],
      [T('📍 Where\n123 Main St', { align: 'center' })],
    ], { bg: '#eff6ff' }),
    BTN('Reserve your spot', { align: 'center' }),
    FOOT('Can’t make it? Reply and we’ll send the recording.'),
  ] },
  { name: 'Welcome series', blocks: [
    H('Welcome aboard! 👋'),
    T('We’re thrilled to have you. Here’s how to get the most out of it in 3 quick steps.'),
    COLS([[T('1️⃣', { align: 'center' })], [T('Set up your profile so things feel like yours.')]], { ratio: '33-67' }),
    COLS([[T('2️⃣', { align: 'center' })], [T('Explore the features that matter most to you.')]], { ratio: '33-67' }),
    COLS([[T('3️⃣', { align: 'center' })], [T('Invite a teammate and get more done together.')]], { ratio: '33-67' }),
    BTN('Get started'),
  ] },
  { name: 'Flash sale', blocks: [
    H('⚡ Flash sale', { align: 'center' }),
    COLS([[H('Save 30%', { level: 2, align: 'center' }), T('This weekend only. Use code FLASH30 at checkout.', { align: 'center' })]], { bg: '#fef9c3' }),
    IMG('Sale'),
    BTN('Shop the sale', { align: 'center', bg: '#dc2626' }),
    FOOT('Sale ends Sunday at midnight.'),
  ] },
  { name: 'We miss you', blocks: [
    H('We miss you 💙', { align: 'center' }),
    T('It’s been a while! Here’s 15% off to welcome you back — because good things are better shared.', { align: 'center' }),
    COLS([[T('Your code\nWELCOME15', { align: 'center' })]], { bg: '#ecfdf5' }),
    BTN('Come back', { align: 'center' }),
    FOOT('Not interested anymore? Unsubscribe — no hard feelings.'),
  ] },
];

/* ===================== editor ===================== */
const PALETTE = [
  { type: 'heading', icon: 'H', label: 'Heading' },
  { type: 'text',    icon: '¶', label: 'Text' },
  { type: 'image',   icon: '🖼', label: 'Image' },
  { type: 'button',  icon: '⬚', label: 'Button' },
  { type: 'divider', icon: '—', label: 'Divider' },
  { type: 'spacer',  icon: '↕', label: 'Spacer' },
];

export default function EmailBlockEditor({ blocks, onChange, userId }) {
  const list = Array.isArray(blocks) ? blocks : [];
  const [overGap, setOverGap] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const setBlock = (i, nb) => onChange(list.map((b, j) => j === i ? nb : b));
  const update = (i, patch) => setBlock(i, { ...list[i], ...patch });
  const remove = (i) => { onChange(list.filter((_, j) => j !== i)); setSelected(null); };
  const move = (i, dir) => { const a = [...list]; const j = i + dir; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; onChange(a); setSelected(j); };
  const applyTemplate = (t) => { onChange(t.blocks.map(b => JSON.parse(JSON.stringify(b)))); setSelected(null); setShowTemplates(false); };

  function handleDrop(idx, e) {
    e.preventDefault(); setOverGap(null);
    const payload = e.dataTransfer.getData('text/plain') || '';
    if (payload.startsWith('new:')) {
      const type = payload.slice(4); if (!blockDefaults[type]) return;
      const next = [...list]; next.splice(idx, 0, newBlock(type)); onChange(next); setSelected(idx);
    } else if (payload.startsWith('move:')) {
      const from = Number(payload.slice(5)); if (Number.isNaN(from)) return;
      const next = [...list]; const [m] = next.splice(from, 1);
      let ins = idx; if (from < idx) ins -= 1; next.splice(ins, 0, m); onChange(next); setSelected(ins);
    }
    setDragIdx(null);
  }
  const allowDrop = (idx) => (e) => { e.preventDefault(); if (overGap !== idx) setOverGap(idx); };

  const Gap = ({ idx }) => (
    <div onDragOver={allowDrop(idx)} onDragLeave={() => setOverGap(g => g === idx ? null : g)} onDrop={(e) => handleDrop(idx, e)}
      style={{ height: overGap === idx ? 26 : 8, borderRadius: 8,
        background: overGap === idx ? '#dbeafe' : 'transparent', outline: overGap === idx ? '2px solid #60a5fa' : 'none',
        transition: 'all .1s ease' }} />
  );

  const insertColumns = (idx, L) => { const next = [...list]; next.splice(idx, 0, makeColumns(L)); onChange(next); };
  const AddZone = ({ idx }) => (
    <div onDragOver={allowDrop(idx)} onDragLeave={() => setOverGap(g => g === idx ? null : g)} onDrop={(e) => handleDrop(idx, e)}
      style={{ border: '2px dashed #cbd5e1', borderRadius: 10, background: overGap === idx ? '#dbeafe' : '#fff', padding: '18px 12px', textAlign: 'center', margin: '4px 0', transition: 'all .1s ease' }}>
      <div style={{ color: '#94a3b8', fontSize: 13.5, marginBottom: 12 }}>{overGap === idx ? 'Drop it here' : 'Drag a block here, or choose columns'}</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        {LAYOUTS.map(L => (
          <button key={L.key} type="button" onClick={() => insertColumns(idx, L)} title={`${L.count} column${L.count > 1 ? 's' : ''}`}
            style={{ padding: 5, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', lineHeight: 0 }}>
            <LayoutIcon parts={L.parts} active={false} />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      {showTemplates && <TemplateModal onPick={applyTemplate} onClose={() => setShowTemplates(false)} />}

      {/* ── palette ── */}
      <div style={{ width: 188, flexShrink: 0, position: 'sticky', top: 12 }}>
        <button type="button" onClick={() => setShowTemplates(true)} style={{ ...tplBtn, width: '100%', padding: '9px 12px', marginBottom: 10 }}>✨ Templates</button>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 2px' }}>Blocks</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {PALETTE.map(p => (
            <div key={p.type} draggable
              onDragStart={(e) => { e.dataTransfer.setData('text/plain', 'new:' + p.type); e.dataTransfer.effectAllowed = 'copy'; }}
              style={paletteCard} title={`Drag in a ${p.label.toLowerCase()}`}>
              <span style={{ fontSize: 18 }}>{p.icon}</span>
              <span style={{ fontSize: 11.5, fontWeight: 600 }}>{p.label}</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, lineHeight: 1.4 }}>Drag a block onto the email →<br />Click a block to edit it.</div>
      </div>

      {/* ── canvas (the live email) ── */}
      <div style={{ flex: 1, minWidth: 0, background: '#eef2f7', borderRadius: 12, padding: '20px 16px', minHeight: 320 }} onClick={() => setSelected(null)}>
        <div onClick={e => e.stopPropagation()} style={{ maxWidth: 600, margin: '0 auto', background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '8px 14px' }}>
          {!list.length ? <AddZone idx={0} /> : (
            <>
              <Gap idx={0} />
              {list.map((b, i) => {
                const dragHandlers = {
                  onDragStart: (e) => { e.dataTransfer.setData('text/plain', 'move:' + i); e.dataTransfer.effectAllowed = 'move'; setDragIdx(i); },
                  onDragEnd: () => { setDragIdx(null); setOverGap(null); },
                };
                return (
                <React.Fragment key={i}>
                  {b.type === 'columns' ? (
                    <CanvasColumns b={b} set={(nb) => setBlock(i, nb)} onMove={(d) => move(i, d)} onRemove={() => remove(i)}
                      dragHandlers={dragHandlers} dragging={dragIdx === i} userId={userId} />
                  ) : selected === i ? (
                    <div style={{ border: '2px solid #2563eb', borderRadius: 8, padding: 12, margin: '4px 0', background: '#fff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span draggable {...dragHandlers}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'grab', userSelect: 'none', fontSize: 12, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.04em' }} title="Drag to reorder">
                          <span style={{ color: '#93c5fd', letterSpacing: '-2px' }}>⠿⠿</span>{BLOCK_LABEL[b.type]}
                        </span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button type="button" onClick={() => move(i, -1)} style={iconBtn} title="Up">↑</button>
                          <button type="button" onClick={() => move(i, 1)} style={iconBtn} title="Down">↓</button>
                          <button type="button" onClick={() => remove(i)} style={{ ...iconBtn, color: '#dc2626' }} title="Delete">✕</button>
                          <button type="button" onClick={() => setSelected(null)} style={{ ...iconBtn, color: '#2563eb', borderColor: '#bfdbfe' }} title="Done">✓</button>
                        </div>
                      </div>
                      <BlockFields b={b} i={i} update={update} userId={userId} />
                    </div>
                  ) : (
                    <div onClick={() => setSelected(i)} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(h => h === i ? null : h)}
                      style={{ position: 'relative', cursor: 'pointer', borderRadius: 6, outline: hover === i ? '2px solid #93c5fd' : '2px solid transparent', outlineOffset: 2, transition: 'outline-color .1s', opacity: dragIdx === i ? 0.4 : 1 }}>
                      {hover === i && (
                        <span style={{ position: 'absolute', top: -10, left: 8, zIndex: 2, background: '#2563eb', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{BLOCK_LABEL[b.type]} · click to edit</span>
                      )}
                      <div style={{ pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: renderBlock(b, true) }} />
                    </div>
                  )}
                  {i < list.length - 1 && <Gap idx={i + 1} />}
                </React.Fragment>
                );
              })}
              <AddZone idx={list.length} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── template gallery modal ── */
function TemplateModal({ onPick, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 980, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 70px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>Choose a template</div>
            <div style={{ fontSize: 13.5, color: '#6b7280' }}>Start with a structured layout and customize it to match your brand.</div>
          </div>
          <button onClick={onClose} style={{ ...iconBtn, fontSize: 16, border: 'none' }}>✕</button>
        </div>
        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16 }}>
          {TEMPLATES.map(t => (
            <div key={t.name} style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ height: 230, overflow: 'hidden', background: '#fff', borderBottom: '1px solid #f1f5f9', position: 'relative' }}>
                <div style={{ width: 600, transform: 'scale(0.35)', transformOrigin: 'top left', padding: 16, pointerEvents: 'none' }}
                  dangerouslySetInnerHTML={{ __html: renderBlocksToHtml(t.blocks, true) }} />
              </div>
              <div style={{ padding: 14, marginTop: 'auto' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 10 }}>{t.name}</div>
                <button onClick={() => onPick(t)} style={{ ...tplBtn, width: '100%', padding: '9px 12px' }}>Use template</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── columns rendered + edited directly on the canvas ── */
function LayoutIcon({ parts, active }) {
  const total = parts.reduce((a, b) => a + b, 0);
  return (
    <div style={{ display: 'flex', gap: 2, width: 30, height: 20, padding: 2, border: `1.5px solid ${active ? '#2563eb' : '#cbd5e1'}`, borderRadius: 4, background: '#fff' }}>
      {parts.map((p, i) => <div key={i} style={{ flex: p / total, background: active ? '#93c5fd' : '#e2e8f0', borderRadius: 1 }} />)}
    </div>
  );
}

function CanvasColumns({ b, set, onMove, onRemove, dragHandlers, dragging, userId }) {
  const cols = (b.columns && b.columns.length) ? b.columns : [[], []];
  const widths = RATIOS[b.ratio] || RATIOS['50-50'];
  const [overCol, setOverCol] = useState(null);
  const [subSel, setSubSel] = useState(null);   // { c, s }
  const [hoverBar, setHoverBar] = useState(false);

  const setCol = (c, arr) => set({ ...b, columns: cols.map((col, k) => k === c ? arr : col) });
  const addSub = (c, type) => { setCol(c, [...cols[c], newBlock(type)]); setSubSel({ c, s: cols[c].length }); };
  const updateSub = (c, s, patch) => setCol(c, cols[c].map((sb, k) => k === s ? { ...sb, ...patch } : sb));
  const removeSub = (c, s) => { setCol(c, cols[c].filter((_, k) => k !== s)); setSubSel(null); };
  const moveSub = (c, s, dir) => { const a = [...cols[c]]; const j = s + dir; if (j < 0 || j >= a.length) return; [a[s], a[j]] = [a[j], a[s]]; setCol(c, a); };
  const dropInto = (c) => (e) => {
    e.preventDefault(); e.stopPropagation(); setOverCol(null);
    const payload = e.dataTransfer.getData('text/plain') || '';
    if (payload.startsWith('new:')) { const type = payload.slice(4); if (blockDefaults[type] && type !== 'columns') addSub(c, type); }
  };
  /* change layout, preserving content (extra columns merge into the last kept one) */
  const changeLayout = (L) => {
    let next = cols.map(c => [...c]);
    if (next.length < L.count) while (next.length < L.count) next.push([]);
    else if (next.length > L.count) { const extra = next.slice(L.count).flat(); next = next.slice(0, L.count); next[L.count - 1] = [...next[L.count - 1], ...extra]; }
    set({ ...b, ratio: L.key, columns: next });
  };

  return (
    <div onMouseEnter={() => setHoverBar(true)} onMouseLeave={() => setHoverBar(false)} onClick={e => e.stopPropagation()}
      style={{ position: 'relative', borderRadius: 8, padding: 4, margin: '2px 0', outline: hoverBar ? '2px solid #cbd5e1' : '2px solid transparent', outlineOffset: 2, opacity: dragging ? 0.4 : 1, transition: 'outline-color .1s' }}>
      {/* toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap', opacity: hoverBar ? 1 : 0.55, transition: 'opacity .1s' }}>
        <span draggable {...dragHandlers} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'grab', userSelect: 'none', fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.04em' }} title="Drag to reorder">
          <span style={{ color: '#93c5fd', letterSpacing: '-2px' }}>⠿⠿</span>Columns
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* visual layout picker */}
          <div style={{ display: 'flex', gap: 3 }}>
            {LAYOUTS.map(L => (
              <button key={L.key} type="button" onClick={() => changeLayout(L)} title={`${L.count} column${L.count > 1 ? 's' : ''}`}
                style={{ padding: 2, background: 'none', border: 'none', cursor: 'pointer', borderRadius: 4, outline: b.ratio === L.key ? '2px solid #bfdbfe' : 'none' }}>
                <LayoutIcon parts={L.parts} active={b.ratio === L.key} />
              </button>
            ))}
          </div>
          <label style={swatch} title="Section background"><input type="color" value={b.bg || '#ffffff'} onChange={e => set({ ...b, bg: e.target.value })} style={colorInp} /></label>
          {b.bg && <button type="button" onClick={() => set({ ...b, bg: '' })} style={iconBtn} title="Clear color">⌫</button>}
          <button type="button" onClick={() => onMove(-1)} style={iconBtn} title="Up">↑</button>
          <button type="button" onClick={() => onMove(1)} style={iconBtn} title="Down">↓</button>
          <button type="button" onClick={onRemove} style={{ ...iconBtn, color: '#dc2626' }} title="Delete">✕</button>
        </div>
      </div>

      {/* columns row */}
      <div style={{ display: 'flex', gap: 8, background: b.bg || 'transparent', borderRadius: 8, padding: b.bg ? 10 : 0 }}>
        {cols.map((col, c) => (
          <div key={c}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (overCol !== c) setOverCol(c); }}
            onDragLeave={() => setOverCol(o => o === c ? null : o)}
            onDrop={dropInto(c)}
            style={{ flex: `0 0 ${widths[c] || (100 / cols.length) + '%'}`, maxWidth: widths[c] || (100 / cols.length) + '%', minWidth: 0,
              border: `2px dashed ${overCol === c ? '#60a5fa' : '#e5e7eb'}`, borderRadius: 8, padding: 6, background: overCol === c ? 'rgba(96,165,250,0.12)' : 'rgba(0,0,0,0.015)' }}>
            {col.map((sb, s) => {
              const isSel = subSel && subSel.c === c && subSel.s === s;
              return isSel ? (
                <div key={s} style={{ border: '2px solid #2563eb', borderRadius: 6, padding: 8, marginBottom: 6, background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase' }}>{BLOCK_LABEL[sb.type]}</span>
                    <div style={{ display: 'flex', gap: 3 }}>
                      <button type="button" onClick={() => moveSub(c, s, -1)} style={iconBtn}>↑</button>
                      <button type="button" onClick={() => moveSub(c, s, 1)} style={iconBtn}>↓</button>
                      <button type="button" onClick={() => removeSub(c, s)} style={{ ...iconBtn, color: '#dc2626' }}>✕</button>
                      <button type="button" onClick={() => setSubSel(null)} style={{ ...iconBtn, color: '#2563eb' }}>✓</button>
                    </div>
                  </div>
                  <BlockFields b={sb} i={0} update={(_, patch) => updateSub(c, s, patch)} userId={userId} />
                </div>
              ) : (
                <div key={s} onClick={() => setSubSel({ c, s })} title="Click to edit"
                  style={{ cursor: 'pointer', borderRadius: 4, padding: 2, marginBottom: 4, outline: '1px dashed transparent', transition: 'outline-color .1s' }}
                  onMouseEnter={e => { e.currentTarget.style.outlineColor = '#93c5fd'; }}
                  onMouseLeave={e => { e.currentTarget.style.outlineColor = 'transparent'; }}>
                  <div style={{ pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: renderBlock(sb, true) }} />
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', padding: '8px 2px' }}>{col.length ? 'Drop a block here' : 'Drag a block here'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── leaf block field editors ── */
function BlockFields({ b, i, update, userId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  async function onFile(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('files', file); fd.append('userId', userId || ''); fd.append('prefix', 'email-images');
      const { data } = await ax.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = data.files?.[0]?.url; if (url) update(i, { url });
    } catch (err) { console.error('upload', err); alert('Image upload failed.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  if (b.type === 'heading') return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <input value={b.text} onChange={e => update(i, { text: e.target.value })} placeholder="Heading text" style={{ ...inp, flex: 1, minWidth: 140 }} />
      <select value={b.level} onChange={e => update(i, { level: Number(e.target.value) })} style={sel}><option value={1}>Large</option><option value={2}>Medium</option></select>
      <AlignSelect b={b} i={i} update={update} />
    </div>
  );
  if (b.type === 'text') return (
    <div>
      <textarea value={b.text} onChange={e => update(i, { text: e.target.value })} rows={3} placeholder="Your text…" style={{ ...inp, resize: 'vertical' }} />
      <div style={{ marginTop: 6 }}><AlignSelect b={b} i={i} update={update} /></div>
    </div>
  );
  if (b.type === 'image') return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} style={{ ...miniBtnSm, opacity: uploading ? 0.6 : 1 }}>{uploading ? 'Uploading…' : 'Upload'}</button>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
        <span style={{ fontSize: 12, color: '#9ca3af' }}>or URL</span>
      </div>
      <input value={b.url} onChange={e => update(i, { url: e.target.value })} placeholder="https://…/image.jpg" style={{ ...inp, marginTop: 8 }} />
      {b.url && <img src={b.url} alt="" style={{ maxWidth: '100%', maxHeight: 110, marginTop: 8, borderRadius: 6, border: '1px solid #f1f5f9' }} />}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <input value={b.href} onChange={e => update(i, { href: e.target.value })} placeholder="Link (optional)" style={{ ...inp, flex: 1, minWidth: 120 }} />
        <select value={b.width} onChange={e => update(i, { width: Number(e.target.value) })} style={sel}><option value={100}>100%</option><option value={75}>75%</option><option value={50}>50%</option></select>
        <AlignSelect b={b} i={i} update={update} />
      </div>
    </div>
  );
  if (b.type === 'button') return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <input value={b.text} onChange={e => update(i, { text: e.target.value })} placeholder="Button text" style={{ ...inp, flex: 1, minWidth: 110 }} />
      <input value={b.href} onChange={e => update(i, { href: e.target.value })} placeholder="https://…" style={{ ...inp, flex: 1, minWidth: 120 }} />
      <label style={swatch} title="Button color"><input type="color" value={b.bg} onChange={e => update(i, { bg: e.target.value })} style={colorInp} /></label>
      <AlignSelect b={b} i={i} update={update} />
    </div>
  );
  if (b.type === 'spacer') return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 13, color: '#374151' }}>Height</span>
      <input type="number" min={0} value={b.height} onChange={e => update(i, { height: Math.max(0, parseInt(e.target.value, 10) || 0) })} style={{ ...inp, width: 90 }} />
      <span style={{ fontSize: 13, color: '#374151' }}>px</span>
    </div>
  );
  return <div style={{ fontSize: 13, color: '#9ca3af' }}>A horizontal divider line.</div>;
}

const AlignSelect = ({ b, i, update }) => (
  <select value={b.align || 'left'} onChange={e => update(i, { align: e.target.value })} style={sel}>
    <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
  </select>
);

/* ── styles ── */
const inp      = { padding: '8px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 13.5, color: '#111827', outline: 'none', boxSizing: 'border-box', width: '100%' };
const sel      = { ...inp, width: 'auto', background: '#fff' };
const miniBtn  = { padding: '6px 11px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const miniBtnSm = { ...miniBtn, padding: '4px 8px', fontSize: 11.5 };
const tplBtn   = { padding: '7px 13px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const paletteCard = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '12px 6px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 9, cursor: 'grab', userSelect: 'none', textAlign: 'center' };
const iconBtn  = { padding: '3px 8px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const swatch   = { width: 34, height: 34, borderRadius: 7, border: '1px solid #d1d5db', overflow: 'hidden', cursor: 'pointer', display: 'inline-flex' };
const colorInp = { width: '150%', height: '150%', margin: '-25%', border: 'none', padding: 0, background: 'none', cursor: 'pointer' };
